import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export const useBatchUpload = (batch, showError, showSuccess) => {
    const [uploadQueue, setUploadQueue] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [localMovies, setLocalMovies] = useState(batch.movies?.map(movie => ({ ...movie, source: 'server' })) || []);
    const [fileErrors, setFileErrors] = useState({});

    // Upload queue processor
    useEffect(() => {
        if (uploadQueue.length > 0 && !isUploading) {
            setIsUploading(true);
            processUploadQueue();
        }
    }, [uploadQueue, isUploading]);

const processUploadQueue = async () => {
    if (uploadQueue.length === 0) {
        setIsUploading(false);
        return;
    }
    const file = uploadQueue[0];
    const formData = new FormData();
    formData.append('file', file);
    // Create temporary movie entry
    const tempMovieId = `temp-${Date.now()}-${Math.random()}-${file.name}`;
    const tempMovie = {
        id: tempMovieId,
        title: file.name.replace(/\.pdf$/i, ''),
        filename: file.name,
        size: file.size,
        status: 'uploading',
        createdAt: new Date().toISOString(),
        source: 'uploading',
        originalFile: file // Store original file for potential retry
    };
    // Remove duplicates by filename before adding new temp movie
    setLocalMovies(prev => {
        const filtered = prev.filter(movie =>
            !(movie.filename === file.name && ['uploading', 'failed'].includes(movie.source))
        );
        return [tempMovie, ...filtered];
    });
    // Remove from queue immediately
    setUploadQueue(prev => prev.slice(1));
    try {
        const response = await axios.post(`/batches/${batch.uuid}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
        });
        if (response.data) {
            console.log('Server response:', response.data);
            setLocalMovies(prev => {
                // Remove temp movie and any duplicates by filename or ID
                const filtered = prev.filter(movie =>
                    movie.id !== tempMovieId &&
                    movie.id !== response.data.id &&
                    movie.filename !== response.data.filename
                );
                return [{ ...response.data, size: file.size, source: 'server' }, ...filtered];
            });
            showSuccess(`Successfully uploaded: ${file.name}`);
        } else {
            throw new Error('Invalid response from server');
        }
    } catch (error) {
        console.error('Upload error:', error);
        let errorMessage = 'Upload failed';
        if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.code === 'ECONNABORTED') {
            errorMessage = 'Upload timeout - file too large or connection issue';
        } else if (error.message) {
            errorMessage = error.message;
        }
        const displayError = errorMessage.length > 50 ? errorMessage.substring(0, 50) + '...' : errorMessage;
        setLocalMovies(prev => prev.map(movie =>
            movie.id === tempMovieId
                ? {
                      ...movie,
                      status: 'failed',
                      source: 'failed',
                      errorPreview: displayError,
                      originalFile: file // Retain original file for retry
                  }
                : movie
        ));
        setFileErrors(prev => ({
            ...prev,
            [tempMovieId]: errorMessage
        }));
        showError(`Failed to upload ${file.name}: ${displayError}`);
    } finally {
        setTimeout(() => {
            setIsUploading(false);
        }, 2000);
    }
};

const handleFileSelect = (files) => {
    try {
        const pdfFiles = files.filter(file => file.type === 'application/pdf');
        if (pdfFiles.length !== files.length) {
            showError('Only PDF files are allowed');
            return false;
        }
        // Check file sizes (20MB limit)
        const oversizedFiles = pdfFiles.filter(file => file.size > 20 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            showError(`Some files exceed the 20MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
            return false;
        }
        // Check for duplicates by filename
        const existingNames = new Set(localMovies.map(movie => movie.filename));
        const queuedNames = new Set(uploadQueue.map(file => file.name));
        const duplicateFiles = [];
        const newFiles = pdfFiles.filter(file => {
            if (existingNames.has(file.name) || queuedNames.has(file.name)) {
                duplicateFiles.push(file.name);
                return false;
            }
            return true;
        });
        if (duplicateFiles.length > 0) {
            showError(`Duplicate files skipped: ${duplicateFiles.join(', ')}`);
        }
        if (newFiles.length > 0) {
            setUploadQueue(prev => [...prev, ...newFiles]);
            showSuccess(`Added ${newFiles.length} file(s) to upload queue`);
            return true;
        }
        return false;
    } catch (error) {
        console.error('File selection error:', error);
        showError('Error selecting files');
        return false;
    }
};

    const handleDeleteQueueItem = (index) => {
        try {
            const fileToRemove = uploadQueue[index];
            setUploadQueue(prev => prev.filter((_, i) => i !== index));
            
            if (fileToRemove) {
                showSuccess(`Removed ${fileToRemove.name} from queue`);
            }
        } catch (error) {
            console.error('Error removing queue item:', error);
            showError('Error removing file from queue');
        }
    };

    return {
        uploadQueue,
        isUploading,
        localMovies,
        fileErrors,
        setLocalMovies,
        setFileErrors,
        handleFileSelect,
        handleDeleteQueueItem
    };
};